// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getMyStandardsViewPreference,
  saveMyStandardsViewPreference,
  listStandardsScopeTimeFrames,
  getEffectiveStandardsVisibility,
} from '../../services/api';
import BackToTop from '../../components/BackToTop';
import FloatingSelectionFooter from '../../components/FloatingSelectionFooter';
import StandardSetModal from '../../components/StandardSetModal';
import { normalizeLiteracyWritingStrand } from '../../utils/progressStatus';
import './StandardsProgressionDropdowns.css';
import {
  buildGradeColumns, expandGradeRange, findGradeColumnIndex, formatGradeColumnLabel,
  getGradeColumnKey, getStandardApplicableGrades,
} from '../../utils/gradeColumns';

const SUBJECT_ORDER = ['Attendance', 'Mathematics', 'Science', 'Success Skills', 'Social Studies', 'Literacy'];
const EMPTY_ROWS = [];

// Set to true to re-enable the Attendance subject in the progression board.
const SHOW_ATTENDANCE = false;
const MAX_VISIBLE_DATA_POINT_DOTS = 10;
const GRADE_BANDS = [
  { label: 'PK', grades: ['PK'] },
  { label: 'K-5', grades: ['K', '1', '2', '3', '4', '5', 'K-2', '3-5'] },
  { label: '6-8', grades: ['6', '7', '8', '6-8'] },
  { label: '9-12', grades: ['9', '10', '11', '12', '9-10', '11-12', '9-12'] },
];
const WHEEL_FILL = '#ffb26b';
const SUBJECT_WHEEL_LAYOUTS = {
  Attendance: [
    {
      key: 'attendance-rate',
      startAngle: 0,
      endAngle: 360,
      displayLabel: 'Attendance Rate',
      fullCircle: true,
      labelRadius: 70,
      labelAngle: 90,
    },
  ],
  Literacy: [
    { key: 'reading-foundations', startAngle: 0, endAngle: 60, displayLabel: 'Reading Foundations', labelRadius: 76, labelAngle: 30 },
    { key: 'reading', startAngle: 51.4, endAngle: 102.8, displayLabel: 'Reading', labelRadius: 76, labelAngle: 77.1 },
    { key: 'reading-literary-text', startAngle: 102.8, endAngle: 154.2, displayLabel: 'Literary Text', labelRadius: 76, labelAngle: 128.5 },
    { key: 'reading-informational-text', startAngle: 154.2, endAngle: 205.6, displayLabel: 'Informational Text', labelRadius: 76, labelAngle: 179.9 },
    { key: 'writing', startAngle: 205.6, endAngle: 257, displayLabel: 'Writing', labelRadius: 76, labelAngle: 231.3 },
    { key: 'language', startAngle: 257, endAngle: 308.4, displayLabel: 'Language', labelRadius: 76, labelAngle: 282.7 },
    { key: 'speaking-and-listening', startAngle: 308.4, endAngle: 360, displayLabel: 'Speaking & Listening', labelRadius: 76, labelAngle: 334.2 },
  ],
  Mathematics: [
    { key: 'number-sense-operations', startAngle: 270, endAngle: 360, displayLabel: 'Numbers and Operations', labelRadius: 84, labelAngle: 315 },
    { key: 'algebraic-relationships', startAngle: 0, endAngle: 90, displayLabel: 'Algebraic Relationships', labelRadius: 84, labelAngle: 45 },
    { key: 'data', startAngle: 90, endAngle: 180, displayLabel: 'Data', labelRadius: 78, labelAngle: 135 },
    { key: 'geometry-measurement', startAngle: 180, endAngle: 270, displayLabel: 'Geometry and Measurement', labelRadius: 84, labelAngle: 225 },
  ],
  Science: [
    { key: 'earth-space', startAngle: 270, endAngle: 360, displayLabel: 'Earth and Space Sciences', labelRadius: 84, labelAngle: 315 },
    { key: 'life-science', startAngle: 0, endAngle: 90, displayLabel: 'Life Sciences', labelRadius: 84, labelAngle: 45 },
    { key: 'physical-science', startAngle: 90, endAngle: 180, displayLabel: 'Physical Sciences', labelRadius: 84, labelAngle: 135 },
    { key: 'engineering', startAngle: 180, endAngle: 270, displayLabel: 'Engineering, Technology, and Application of Science', labelRadius: 84, labelAngle: 225 },
  ],
  'Social Studies': [
    { key: 'history', startAngle: 0, endAngle: 60, displayLabel: 'History', labelRadius: 80, labelAngle: 30 },
    { key: 'geography', startAngle: 45, endAngle: 90, displayLabel: 'Geography', labelRadius: 78, labelAngle: 67.5 },
    { key: 'economics', startAngle: 90, endAngle: 135, displayLabel: 'Economics', labelRadius: 78, labelAngle: 112.5 },
    { key: 'government', startAngle: 135, endAngle: 180, displayLabel: 'Government', labelRadius: 78, labelAngle: 157.5 },
    { key: 'american-history', startAngle: 180, endAngle: 225, displayLabel: 'American History', labelRadius: 76, labelAngle: 202.5 },
    { key: 'world-history', startAngle: 225, endAngle: 270, displayLabel: 'World History', labelRadius: 76, labelAngle: 247.5 },
    { key: 'relationships', startAngle: 270, endAngle: 315, displayLabel: 'Individuals & Groups', labelRadius: 76, labelAngle: 292.5 },
    { key: 'inquiry', startAngle: 315, endAngle: 360, displayLabel: 'Social Inquiry', labelRadius: 76, labelAngle: 337.5 },
  ],
  'Success Skills': [
    { key: 'social-emotional', startAngle: 270, endAngle: 30, displayLabel: 'Social and Emotional Development', labelRadius: 84, labelAngle: 330 },
    { key: 'study-skills', startAngle: 30, endAngle: 150, displayLabel: 'Academic Development', labelRadius: 84, labelAngle: 90 },
    { key: 'goal-character', startAngle: 150, endAngle: 270, displayLabel: 'Career Development', labelRadius: 84, labelAngle: 210 },
  ],
};

const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));
const CANONICAL_GRADE_SEQUENCE = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

function getStrandDescription(subject, strandCode) {
  if (!strandCode) return '';
  
  const STRAND_DESCRIPTIONS = {
    'Literacy': {
      'RF': 'Reading Foundations',
      'RL': 'Reading Literature',
      'RI': 'Reading Informational Text',
      'W': 'Writing',
      'SL': 'Speaking and Listening',
      'L': 'Language',
    },
    'Mathematics': {
      'NS': 'Number Sense',
      'NBT': 'Number Sense and Operations in Base Ten',
      'NF': 'Number Sense and Operations in Fractions',
      'RA': 'Relationships and Algebraic Thinking',
      'EEI': 'Expressions, Equations and Inequalities',
      'RP': 'Ratios and Proportional Relationships',
      'F': 'Functions',
      'GM': 'Geometry and Measurement',
      'DS': 'Data and Statistics',
      'DSP': 'Data Analysis, Statistics and Probability',
    },
    'Science': {
      'ESS': 'Earth and Space Science',
      'LS': 'Life Science',
      'PS': 'Physical Science',
      'ETS': 'Engineering, Technology, and Applications of Science',
    },
    'Social Studies': {
      'H': 'History',
      'G': 'Geography',
      'E': 'Economics',
      'C': 'Civics and Government',
    },
    'Success Skills': {
      'SEL': 'Social and Emotional Learning',
      'SAS': 'Study and Academic Skills',
      'GC': 'Goal Setting and Character',
    },
  };
  
  return STRAND_DESCRIPTIONS[subject]?.[strandCode] || strandCode;
}

function normalizeDomainKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function normalizeClusterLabel(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function findDomainLayout(subject, domain) {
  const domainCode = String(domain || '').trim().toUpperCase();
  const normalized = normalizeDomainKey(domain);
  const layouts = SUBJECT_WHEEL_LAYOUTS[subject] || [];

  return layouts.find((layout) => {
    if (layout.key === normalized) return true;
    
    // Mathematics
    if (layout.key === 'number-sense-operations') {
      return domainCode === 'NO' || (normalized.includes('number') && normalized.includes('operation'));
    }
    if (layout.key === 'algebraic-relationships') {
      return domainCode === 'AR' || normalized.includes('algebra') || normalized.includes('function') || normalized.includes('relationship');
    }
    if (layout.key === 'geometry-measurement') {
      return domainCode === 'GM' || normalized.includes('geometry') || normalized.includes('measurement');
    }
    if (layout.key === 'data') {
      return domainCode === 'D' || normalized.includes('data') || normalized.includes('statistics');
    }
    
    // Literacy - match by domain code
    if (layout.key === 'reading-foundations') {
      return domainCode === 'RF' || (normalized.includes('foundation') && !normalized.includes('literary') && !normalized.includes('informational'));
    }
    if (layout.key === 'reading') {
      return (domainCode === 'R' && domainCode.length === 1) || (normalized === 'reading' || normalized === 'r');
    }
    if (layout.key === 'reading-literary-text') {
      return domainCode === 'RL' || normalized.includes('literary');
    }
    if (layout.key === 'reading-informational-text') {
      return domainCode === 'RI' || normalized.includes('informational');
    }
    if (layout.key === 'writing') {
      return domainCode === 'W' || normalized === 'writing';
    }
    if (layout.key === 'language') {
      return domainCode === 'L' || normalized === 'language';
    }
    if (layout.key === 'speaking-and-listening') {
      return domainCode === 'SL' || (normalized.includes('speaking') && normalized.includes('listening'));
    }
    if (layout.key === 'grammar') {
      return normalized.includes('grammar');
    }
    
    // Science
    if (layout.key === 'earth-space') {
      return domainCode === 'ESS' || normalized.includes('earth') || normalized.includes('space');
    }
    if (layout.key === 'life-science') {
      return domainCode === 'LS' || normalized.includes('life');
    }
    if (layout.key === 'physical-science') {
      return domainCode === 'PS' || normalized.includes('physical') || normalized.includes('matter') || normalized.includes('energy');
    }
    if (layout.key === 'engineering') {
      return domainCode === 'ETS' || normalized.includes('engineering') || normalized.includes('technology');
    }
    
    // Social Studies
    if (layout.key === 'american-history') {
      return normalized === 'american-history';
    }
    if (layout.key === 'world-history') {
      return normalized === 'world-history';
    }
    if (layout.key === 'history') {
      return domainCode === 'H' || normalized === 'history';
    }
    if (layout.key === 'geography') {
      return domainCode === 'GEO' || domainCode === 'G' || normalized.includes('geography') || normalized.includes('geographic');
    }
    if (layout.key === 'economics') {
      return domainCode === 'E' || domainCode === 'EG' || normalized.includes('economic');
    }
    if (layout.key === 'government') {
      return domainCode === 'GS' || domainCode === 'GV' || domainCode === 'PC' || domainCode === 'C'
        || normalized.includes('civic') || normalized.includes('government');
    }
    if (layout.key === 'relationships') {
      return domainCode === 'RI' || normalized.includes('relationship') || (normalized.includes('individual') && normalized.includes('group'));
    }
    if (layout.key === 'inquiry') {
      return domainCode === 'TS' || normalized.includes('inquiry') || normalized.includes('tool') || normalized.includes('social-science');
    }
    
    // Success Skills
    if (layout.key === 'social-emotional') {
      return domainCode === 'SED' || normalized.includes('social') || normalized.includes('emotional');
    }
    if (layout.key === 'study-skills') {
      return domainCode === 'AD' || normalized.includes('study') || normalized.includes('academic');
    }
    if (layout.key === 'goal-character') {
      return domainCode === 'CD' || normalized.includes('goal') || normalized.includes('character') || normalized.includes('career');
    }
    
    return false;
  });
}

function normalizeDisplayDomain(subject, domain) {
  const domainCode = String(domain || '').trim().toUpperCase();
  const normalized = normalizeDomainKey(domain);

  if (subject === 'Literacy') {
    // Match by domain code first for accuracy
    if (domainCode === 'RF') return 'Reading Foundations';
    if (domainCode === 'R' && domainCode.length === 1) return 'Reading';
    if (domainCode === 'RL') return 'Reading Literary Text';
    if (domainCode === 'RI') return 'Reading Informational Text';
    if (domainCode === 'W') return 'Writing';
    if (domainCode === 'L') return 'Language';
    if (domainCode === 'SL') return 'Speaking and Listening';
    
    // Fallback to text matching
    if (normalized.includes('foundation')) return 'Reading Foundations';
    if (normalized.includes('literary')) return 'Reading Literary Text';
    if (normalized.includes('informational')) return 'Reading Informational Text';
    if (normalized.includes('writing')) return 'Writing';
    if (normalized.includes('language')) return 'Language';
    if (normalized.includes('speaking') && normalized.includes('listening')) return 'Speaking and Listening';
    if (normalized.includes('reading')) return 'Reading';
    
    // Default fallback
    return domain;
  }

  if (subject === 'Mathematics') {
    // Match by domain code first
    if (domainCode === 'NO') return 'Numbers and Operations';
    if (domainCode === 'AR') return 'Algebraic Relationships';
    if (domainCode === 'GM') return 'Geometry and Measurement';
    if (domainCode === 'D') return 'Data';
    
    // Fallback to text matching
    if (normalized.includes('data') || normalized.includes('statistics') || normalized.includes('probability')) {
      return 'Data';
    }
    if (
      normalized.includes('algebra') ||
      normalized.includes('relationship') ||
      normalized.includes('function') ||
      normalized.includes('equation') ||
      normalized.includes('inequalit') ||
      normalized.includes('ratio') ||
      normalized.includes('proportion') ||
      normalized.includes('pattern')
    ) {
      return 'Algebraic Relationships';
    }
    if (normalized.includes('geometry') || normalized.includes('measurement') || normalized.includes('spatial')) {
      return 'Geometry and Measurement';
    }
    if (normalized.includes('number') || normalized.includes('operation') || normalized.includes('fraction')) {
      return 'Numbers and Operations';
    }
  }

  if (subject === 'Science') {
    // Match by domain code first
    if (domainCode === 'ESS') return 'Earth and Space Sciences';
    if (domainCode === 'LS') return 'Life Sciences';
    if (domainCode === 'PS') return 'Physical Sciences';
    if (domainCode === 'ETS') return 'Engineering, Technology, and Application of Science';
    
    // Fallback to text matching
    if (normalized.includes('earth') || normalized.includes('space')) return 'Earth and Space Sciences';
    if (normalized.includes('life') || normalized.includes('biology')) return 'Life Sciences';
    if (normalized.includes('physical') || normalized.includes('physics') || normalized.includes('chemistry')) return 'Physical Sciences';
    if (normalized.includes('engineering') || normalized.includes('technology')) return 'Engineering, Technology, and Application of Science';
  }

  if (subject === 'Social Studies') {
    // Match by domain code first
    if (domainCode === 'H') return 'History';
    if (domainCode === 'GEO' || domainCode === 'G') return 'Geography';
    if (domainCode === 'E' || domainCode === 'EG') return 'Economics';
    if (domainCode === 'GS' || domainCode === 'GV' || domainCode === 'PC' || domainCode === 'C') return 'Government';
    if (domainCode === 'RI') return 'Relationships of Individuals and Groups';
    if (domainCode === 'TS') return 'Tools of Social Science Inquiry';
    
    // Fallback to text matching
    if (normalized.includes('history')) return 'History';
    if (normalized.includes('geography') || normalized.includes('geographic')) return 'Geography';
    if (normalized.includes('economic')) return 'Economics';
    if (normalized.includes('civic') || normalized.includes('government')) return 'Government';
    if (normalized.includes('relationship') || (normalized.includes('individual') && normalized.includes('group'))) return 'Relationships of Individuals and Groups';
    if (normalized.includes('inquiry') || normalized.includes('tool') || normalized.includes('social-science')) return 'Tools of Social Science Inquiry';
  }

  if (subject === 'Success Skills') {
    // Match by domain code first
    if (domainCode === 'SED') return 'Social and Emotional Development';
    if (domainCode === 'AD') return 'Academic Development';
    if (domainCode === 'CD') return 'Career Development';
    
    // Fallback to text matching
    if (normalized.includes('social') || normalized.includes('emotional')) return 'Social and Emotional Development';
    if (normalized.includes('study') || normalized.includes('academic')) return 'Academic Development';
    if (normalized.includes('goal') || normalized.includes('character') || normalized.includes('career')) return 'Career Development';
  }

  return domain;
}

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

function createSlicePath(centerX, centerY, radius, startAngle, endAngle) {
  const start = polarToCartesian(centerX, centerY, radius, endAngle);
  const end = polarToCartesian(centerX, centerY, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

  return [
    `M ${centerX} ${centerY}`,
    `L ${start.x} ${start.y}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 0 ${end.x} ${end.y}`,
    'Z',
  ].join(' ');
}

function getInitialGrade(initialGrade) {
  const normalized = String(initialGrade || '').toUpperCase().trim();
  if (CANONICAL_GRADE_SEQUENCE.includes(normalized)) return normalized;
  return expandGradeRange(normalized).find(grade => CANONICAL_GRADE_SEQUENCE.includes(grade)) || 'K';
}

function getSubjectShortLabel(subject) {
  return subject === 'Mathematics' ? 'Math' : subject;
}

function bandLabelToAnchorGrade(label) {
  if (label === 'PK') return 'PK';
  if (label === 'K-5') return 'K';
  if (label === '6-8') return '6';
  if (label === '9-12') return '9';
  return 'K';
}

function getGradeRank(grade) {
  const normalized = String(grade || '').toUpperCase().trim();
  const directIndex = CANONICAL_GRADE_SEQUENCE.indexOf(normalized);
  if (directIndex !== -1) return directIndex;

  const rangeGrades = expandGradeRange(normalized);
  const first = rangeGrades[0];
  const firstIndex = CANONICAL_GRADE_SEQUENCE.indexOf(first);
  if (firstIndex !== -1) return firstIndex;

  return 999;
}

export { getGradeColumnKey, buildGradeColumns, findGradeColumnIndex };

function formatAttendanceDescription(metric) {
  const percent = Number.isFinite(metric?.attendance_percent)
    ? `${metric.attendance_percent.toFixed(1)}%`
    : 'No attendance marked yet';
  const present = Number(metric?.present_count || 0);
  const marked = Number(metric?.marked_count || 0);
  const students = Number(metric?.student_count || 0);
  if (!marked) {
    return `${percent} · ${students} student${students === 1 ? '' : 's'} in band`;
  }
  return `${percent} · ${present}/${marked} marked present`;
}

function createSelectionEntry(entry) {
  return {
    id: entry.key,
    code: entry.code,
    subject: entry.subject,
    domain: entry.domain,
    strand: entry.strand,
    strandDescription: entry.strandDescription,
    standardDescription: entry.description || entry.standardDescription || '',
    clusterExpectationDescription: entry.clusterExpectationDescription,
  };
}

export default function StandardsProgressionDropdowns({
  data = EMPTY_ROWS,
  attendanceMetrics = EMPTY_ROWS,
  onChange,
  enableContinueFlow = false,
  onContinue,
  continueLabel = 'Continue to Data Entry →',
  showScorePresetHint = true,
  progressByStandard = {},
  showProgress = true,
  title = 'Standard Progressions',
  allowCellInteraction = true,
  initialGrade = '',
  initialSubject = '',
  initialDomain = '',
  initialStrand = '',
  initialExpandedDomains = {},
  initialExpandedStrands = {},
  focusedStandardCode = '',
}: any) {
  const navigate = useNavigate();
  const [selectedSubject, setSelectedSubject] = useState(initialSubject || '');
  const [selectedDomain, setSelectedDomain] = useState(initialDomain || '');
  const [selectedGrade, setSelectedGrade] = useState(getInitialGrade(initialGrade));
  const gradeSwipeStart = useRef<any>(null);
  const selectedBandLabel = GRADE_BANDS.find(band => band.grades.includes(selectedGrade)).label;
  const [selectedStrandFilter, setSelectedStrandFilter] = useState(initialStrand || '');
  const [standardSearchQuery, setStandardSearchQuery] = useState<any>('');
  const [selectedProgressionEntries, setSelectedProgressionEntries] = useState<any>([]);
  const [bulkSelectionColor, setBulkSelectionColor] = useState<any>('');
  const [isSelectionPanelCollapsed, setIsSelectionPanelCollapsed] = useState<any>(false);
  const [expandedDomains, setExpandedDomains] = useState(initialExpandedDomains);
  const [expandedStrands, setExpandedStrands] = useState(initialExpandedStrands);
  const focusedStandardRef = useRef('');

  const [viewMode, setViewMode] = useState('all');
  const [preferredTimeFrameType, setPreferredTimeFrameType] = useState<any>('');
  const [preferredTimeFrameLabel, setPreferredTimeFrameLabel] = useState<any>('');
  const [availableTimeFrames, setAvailableTimeFrames] = useState<any>([]);
  const [timeFramesUnavailableReason, setTimeFramesUnavailableReason] = useState<any>(null);
  const [effectiveVisibleCodes, setEffectiveVisibleCodes] = useState<any>(null);
  const [savingPreference, setSavingPreference] = useState<any>(false);
  const [preferenceError, setPreferenceError] = useState<any>('');
  const [isSetModalOpen, setIsSetModalOpen] = useState<any>(false);
  const [editingSet, setEditingSet] = useState<any>(null);

  const activeCustomSet = useMemo(() => {
    if (viewMode !== 'preferred' || !preferredTimeFrameType || !preferredTimeFrameLabel) return null;
    return availableTimeFrames.find(
      (tf) => tf.is_custom && tf.time_frame_type === preferredTimeFrameType && tf.time_frame_label === preferredTimeFrameLabel
    ) || null;
  }, [viewMode, preferredTimeFrameType, preferredTimeFrameLabel, availableTimeFrames]);

  const handleSetModalSaved = async (savedSet) => {
    setIsSetModalOpen(false);
    setEditingSet(null);
    setSavingPreference(true);
    setPreferenceError('');
    try {
      await saveMyStandardsViewPreference({
        viewMode: 'preferred',
        timeFrameType: savedSet.time_frame_type,
        timeFrameLabel: savedSet.time_frame_label,
      });
      setViewMode('preferred');
      setPreferredTimeFrameType(savedSet.time_frame_type);
      setPreferredTimeFrameLabel(savedSet.time_frame_label);

      const tfData = await listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: selectedBandLabel });
      setAvailableTimeFrames(tfData.time_frames || []);

      const effData = await getEffectiveStandardsVisibility({ subject: selectedSubject, gradeBand: selectedBandLabel });
      if (effData.mode === 'preferred' && Array.isArray(effData.mls_codes)) {
        setEffectiveVisibleCodes(new Set(effData.mls_codes));
      } else {
        setEffectiveVisibleCodes(null);
      }
    } catch (e: any) {
      setPreferenceError(e.message || 'Failed to update standard set view');
    } finally {
      setSavingPreference(false);
    }
  };

  const handleSetModalDeleted = async () => {
    setIsSetModalOpen(false);
    setEditingSet(null);
    try {
      await saveMyStandardsViewPreference({ viewMode: 'all', timeFrameType: null, timeFrameLabel: null });
      setViewMode('all');
      setPreferredTimeFrameType('');
      setPreferredTimeFrameLabel('');
      setEffectiveVisibleCodes(null);

      const tfData = await listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: selectedBandLabel });
      setAvailableTimeFrames(tfData.time_frames || []);
    } catch (_e: any) {}
  };

  useEffect(() => {
    let cancelled = false;
    getMyStandardsViewPreference()
      .then((pref) => {
        if (cancelled) return;
        setViewMode(pref.view_mode || 'all');
        setPreferredTimeFrameType(pref.time_frame_type || '');
        setPreferredTimeFrameLabel(pref.time_frame_label || '');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedSubject || !selectedBandLabel) {
      setAvailableTimeFrames([]);
      setTimeFramesUnavailableReason(null);
      return;
    }
    let cancelled = false;
    listStandardsScopeTimeFrames({ subject: selectedSubject, gradeBand: selectedBandLabel })
      .then((data) => {
        if (cancelled) return;
        setAvailableTimeFrames(data.time_frames || []);
        setTimeFramesUnavailableReason(data.reason || null);
      })
      .catch(() => {
        if (!cancelled) {
          setAvailableTimeFrames([]);
          setTimeFramesUnavailableReason(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSubject, selectedBandLabel]);

  useEffect(() => {
    if (viewMode !== 'preferred' || !preferredTimeFrameType || !preferredTimeFrameLabel || !selectedSubject || !selectedBandLabel) {
      setEffectiveVisibleCodes(null);
      return;
    }
    let cancelled = false;
    getEffectiveStandardsVisibility({ subject: selectedSubject, gradeBand: selectedBandLabel })
      .then((data) => {
        if (cancelled) return;
        setEffectiveVisibleCodes(data.mode === 'preferred' && Array.isArray(data.mls_codes) ? new Set(data.mls_codes) : null);
      })
      .catch(() => {
        if (!cancelled) setEffectiveVisibleCodes(null);
      });
    return () => {
      cancelled = true;
    };
  }, [viewMode, preferredTimeFrameType, preferredTimeFrameLabel, selectedSubject, selectedBandLabel]);

  const handleViewModeChange = async (nextMode) => {
    setPreferenceError('');
    setViewMode(nextMode);
    if (nextMode === 'all') {
      setPreferredTimeFrameType('');
      setPreferredTimeFrameLabel('');
      setSavingPreference(true);
      try {
        await saveMyStandardsViewPreference({ viewMode: 'all', timeFrameType: null, timeFrameLabel: null });
      } catch (err: any) {
        setPreferenceError(err.message || 'Failed to save your view preference.');
      } finally {
        setSavingPreference(false);
      }
    }
  };

  const handleTimeFrameChange = async (value) => {
    const [type, label] = value.split('::');
    setPreferredTimeFrameType(type || '');
    setPreferredTimeFrameLabel(label || '');
    if (!type || !label) return;
    setSavingPreference(true);
    setPreferenceError('');
    try {
      await saveMyStandardsViewPreference({ viewMode: 'preferred', timeFrameType: type, timeFrameLabel: label });
    } catch (err: any) {
      setPreferenceError(err.message || 'Failed to save your view preference.');
    } finally {
      setSavingPreference(false);
    }
  };

  const normalizedData = useMemo(() => {
    const standardsRows = data
      .map((row, index) => {
        const subject = row.subject ?? '';
        const rawDomain = row.domain ?? '';
        // Prioritize domain_description from database, fallback to normalized display
        const domainDescription = row.domain_description || normalizeDisplayDomain(subject, rawDomain) || rawDomain;
        const displayDomain = domainDescription; // Use description as the display value

        const normalizedGrade = String(row.grade_level ?? row.grade ?? row.gradeLevel ?? '').replace(/^([0-9]+)\s+to\s+([0-9]+)$/i, '$1-$2');
        const standardCode = row.mls_code ?? row.standard_code ?? row.code ?? '';
        const applicableGrades = getStandardApplicableGrades(normalizedGrade, standardCode);
        const sourceStrandDescription = row.strand_description || row.strandDescription || getStrandDescription(subject, row.strand) || row.strand || '';
        const writingStrand = normalizeLiteracyWritingStrand(subject, normalizedGrade, standardCode, sourceStrandDescription);
        const normalizedStrandDescription = writingStrand || sourceStrandDescription;
        const strandKey = subject === 'Social Studies'
          ? normalizeClusterLabel(normalizedStrandDescription) || row.strand || ''
          : writingStrand || row.strand || '';

        return {
          id: row.id ?? `progression-${index}`,
          subject,
          grade: normalizedGrade,
          applicableGrades,
          domain: displayDomain,
          domainDescription: domainDescription,
          domainCode: rawDomain, // Keep original code for reference
          strand: row.strand ?? '',
          strandKey,
          strandDescription: normalizedStrandDescription,
          strandCode: row.strand ?? '', // Keep original strand code
          cluster: row.cluster_and_expectation_number ?? '',
          clusterDescription: row.cluster_expectation_description ?? '',
          code: standardCode,
          // For cells: prefer the specific standard description; fall back to the
          // cluster description when no standard-level text is available (115 standards).
          description: row.standard_description || row.standardDescription || row.cluster_expectation_description || '',
        };
      })
      .filter((row) => row.subject && row.grade && row.domain && row.strand && row.code);

    // Deduplicate by MLS code — keep the first occurrence so the same standard
    // never appears twice even if the API returns duplicate DB rows.
    const seenCodes = new Set();
    const dedupedRows = standardsRows.filter((row) => {
      if (seenCodes.has(row.code)) return false;
      seenCodes.add(row.code);
      return true;
    });

    const attendanceRows = SHOW_ATTENDANCE
      ? (Array.isArray(attendanceMetrics) ? attendanceMetrics : []).map((metric, index) => {
      const gradeBand = metric?.grade_band || 'K-5';
      return {
        id: `attendance-${metric?.class_id || 'class'}-${gradeBand}-${index}`,
        subject: 'Attendance',
        grade: bandLabelToAnchorGrade(gradeBand),
        applicableGrades: expandGradeRange(gradeBand),
        domain: 'Attendance',
        domainDescription: 'Attendance',
        domainCode: 'ATT',
        strand: 'Class Attendance',
        strandDescription: 'Class Attendance',
        strandCode: 'ATT',
        cluster: `${metric?.class_id || 'class'}-${gradeBand}`,
        clusterDescription: `${metric?.class_name || `Class ${metric?.class_id || ''}`} · ${gradeBand}`,
        code: `ATT-${metric?.class_id || index}`,
        description: formatAttendanceDescription(metric),
      };
    })
      : [];

    return [...dedupedRows, ...(SHOW_ATTENDANCE ? attendanceRows : [])];
  }, [data, attendanceMetrics]);

  // Only grades that have standards for the selected subject are offered (e.g. Social Studies has no PK).
  const availableGrades = useMemo(() => {
    const subjectGrades = new Set(
      normalizedData
        .filter((row) => row.subject === selectedSubject)
        .flatMap((row) => row.applicableGrades),
    );
    const grades = CANONICAL_GRADE_SEQUENCE.filter((grade) => subjectGrades.has(grade));
    return grades.length ? grades : CANONICAL_GRADE_SEQUENCE;
  }, [normalizedData, selectedSubject]);

  useEffect(() => {
    if (availableGrades.includes(selectedGrade)) return;
    const selectedIndex = CANONICAL_GRADE_SEQUENCE.indexOf(selectedGrade);
    const nextAvailable = availableGrades.find((grade) => CANONICAL_GRADE_SEQUENCE.indexOf(grade) > selectedIndex);
    setSelectedGrade(nextAvailable || availableGrades[availableGrades.length - 1]);
  }, [availableGrades, selectedGrade]);

  const gradeColumns = useMemo(() => buildGradeColumns(
    normalizedData.filter((row) => row.subject === selectedSubject),
  ), [normalizedData, selectedSubject]);
  const selectedColumnIndex = Math.max(0, findGradeColumnIndex(gradeColumns, selectedGrade));
  const selectGradeColumn = (column) => {
    if (!column) return;
    setSelectedGrade((current) => (column.grades.includes(current) ? current : column.grades[0]));
  };
  const moveGrade = direction => {
    const nextIndex = Math.max(0, Math.min(gradeColumns.length - 1, selectedColumnIndex + direction));
    if (nextIndex !== selectedColumnIndex) selectGradeColumn(gradeColumns[nextIndex]);
  };
  const visibleColumns = useMemo(
    () => gradeColumns.slice(Math.max(0, selectedColumnIndex - 1), selectedColumnIndex + 2),
    [gradeColumns, selectedColumnIndex],
  );
  const visibleColumnKeys = useMemo(() => new Set(visibleColumns.map((column) => column.key)), [visibleColumns]);
  const visibleGrades = useMemo(() => {
    const grades = visibleColumns.length ? new Set(visibleColumns.flatMap((column) => column.grades)) : new Set([selectedGrade]);
    return CANONICAL_GRADE_SEQUENCE.filter((grade) => grades.has(grade));
  }, [visibleColumns, selectedGrade]);
  const focusedColumnKey = gradeColumns[selectedColumnIndex]?.key || selectedGrade;

  const subjectCards = useMemo(() => {
    return unique(normalizedData.map((row) => row.subject))
      .sort((left, right) => {
        const leftIndex = SUBJECT_ORDER.indexOf(left);
        const rightIndex = SUBJECT_ORDER.indexOf(right);
        if (leftIndex === -1 && rightIndex === -1) return left.localeCompare(right);
        if (leftIndex === -1) return 1;
        if (rightIndex === -1) return -1;
        return leftIndex - rightIndex;
      })
      .map((subject) => ({
        subject,
        shortLabel: getSubjectShortLabel(subject),
        domains: unique(
          normalizedData
            .filter((row) => row.subject === subject && row.applicableGrades.some(grade => visibleGrades.includes(grade)))
            .map((row) => row.domain)
        ),
      }));
  }, [normalizedData, visibleGrades]);

  const subjectWheels = useMemo(() => {
    return subjectCards.map((card) => {
      const subjectLayout = SUBJECT_WHEEL_LAYOUTS[card.subject] || [];
      const decoratedDomains = card.domains.map((domain, index) => {
        const layout = findDomainLayout(card.subject, domain);
        const layoutIndex = layout ? subjectLayout.findIndex((item) => item.key === layout.key) : -1;
        return {
          domain,
          layout,
          sourceIndex: index,
          sortIndex: layoutIndex >= 0 ? layoutIndex : 999 + index,
        };
      });

      const orderedDomains = decoratedDomains.sort((left, right) => {
        if (left.sortIndex !== right.sortIndex) return left.sortIndex - right.sortIndex;
        return left.sourceIndex - right.sourceIndex;
      });

      const fallbackStart = 270;
      const fallbackSliceAngle = orderedDomains.length > 0 ? 360 / orderedDomains.length : 360;

      return {
        ...card,
        slices: orderedDomains.map(({ domain, layout }, index) => {
          const startAngle = fallbackStart + index * fallbackSliceAngle;
          const endAngle = startAngle + fallbackSliceAngle;
          const sliceAngle = Math.abs(endAngle - startAngle) || fallbackSliceAngle;
          const midAngle = startAngle + (endAngle - startAngle) / 2;
          const labelAngle = midAngle;
          
          // Adjust label radius based on slice size for better positioning
          let labelRadius = layout?.labelRadius ?? 72;
          if (orderedDomains.length >= 6) {
            labelRadius = 81;
          } else if (sliceAngle <= 60) {
            labelRadius = Math.min(labelRadius, 68); // Smaller slices: position closer to center
          } else if (sliceAngle >= 120) {
            labelRadius = Math.max(labelRadius, 75); // Larger slices: position further out
          }
          
          const labelPoint = polarToCartesian(110, 110, labelRadius, labelAngle);
          
          // Calculate rotation to align label with slice angle
          // For better readability, keep text horizontal or slightly angled
          let labelRotation = 0;
          if (!layout?.fullCircle) {
            const normalizedAngle = labelAngle % 360;
            // Rotate labels in the bottom half to be readable
            if (normalizedAngle > 90 && normalizedAngle < 270) {
              labelRotation = normalizedAngle - 180;
            } else if (normalizedAngle >= 270 || normalizedAngle <= 90) {
              labelRotation = normalizedAngle;
            }
            // Keep rotation minimal for readability
            labelRotation = 0;
          }

          return {
            domain,
            subject: card.subject,
            index,
            path: createSlicePath(110, 110, 102, startAngle, endAngle),
            color: WHEEL_FILL,
            displayLabel: layout?.displayLabel || domain,
            fullCircle: Boolean(layout?.fullCircle),
            sliceAngle,
            labelX: labelPoint.x,
            labelY: labelPoint.y,
            labelRotation,
            labelClassName: layout ? `subject-wheel-label-${layout.key}` : '',
          };
        }),
      };
    });
  }, [subjectCards]);

  const selectedSubjectCard = subjectCards.find((card) => card.subject === selectedSubject);
  const selectedSubjectWheel = subjectWheels.find((card) => card.subject === selectedSubject);

  useEffect(() => {
    // Data loads asynchronously; don't clobber a restored selection before it arrives.
    if (!normalizedData.length) {
      return;
    }

    if (!subjectCards.length) {
      if (selectedSubject) {
        setSelectedSubject('');
      }
      return;
    }

    if (!selectedSubject || !subjectCards.some((card) => card.subject === selectedSubject)) {
      setSelectedSubject(subjectCards[0].subject);
    }
  }, [subjectCards, selectedSubject, normalizedData.length]);

  useEffect(() => {
    if (!normalizedData.length) {
      return;
    }

    if (!selectedSubjectCard) {
      if (selectedDomain) {
        setSelectedDomain('');
      }
      return;
    }

    if (selectedDomain && selectedSubjectCard.domains.includes(selectedDomain)) {
      return;
    }

    setSelectedDomain('');
  }, [selectedSubjectCard, selectedDomain, normalizedData.length]);

  const subjectRows = useMemo(() => {
    return normalizedData.filter((row) => row.subject === selectedSubject);
  }, [normalizedData, selectedSubject]);

  const bandRows = useMemo(() => {
    const rows = subjectRows.filter((row) => visibleColumnKeys.has(getGradeColumnKey(row.applicableGrades)));
    if (viewMode === 'preferred' && effectiveVisibleCodes) {
      return rows.filter((row) => row.subject === 'Attendance' || effectiveVisibleCodes.has(row.code));
    }
    return rows;
  }, [subjectRows, visibleColumnKeys, viewMode, effectiveVisibleCodes]);

  const clusterRows = useMemo(() => {
    if (!selectedDomain) {
      return bandRows;
    }
    return bandRows.filter((row) => row.domain === selectedDomain);
  }, [bandRows, selectedDomain]);

  const searchableClusterRows = useMemo(() => {
    const query = standardSearchQuery.trim().toLowerCase();
    if (!query) {
      return clusterRows;
    }

    return clusterRows.filter((row) => {
      const searchableText = [
        row.code,
        row.description,
        row.domainDescription,
        row.strandDescription,
        row.clusterDescription,
        row.subject,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(query);
    });
  }, [clusterRows, standardSearchQuery]);

  const strandOptions = useMemo(() => unique(searchableClusterRows.map((row) => row.strandDescription)), [searchableClusterRows]);

  useEffect(() => {
    if (!normalizedData.length) {
      return;
    }
    if (selectedStrandFilter && !strandOptions.includes(selectedStrandFilter)) {
      setSelectedStrandFilter('');
    }
  }, [selectedStrandFilter, strandOptions, normalizedData.length]);

  useEffect(() => {
    setSelectedProgressionEntries([]);
  }, [selectedSubject, selectedDomain, selectedGrade, selectedStrandFilter]);

  useEffect(() => {
    if (!onChange) {
      return;
    }

    onChange({
      gradeLevels: visibleGrades,
      subject: selectedSubject,
      domainDescription: selectedDomain,
      strandDescription: selectedStrandFilter,
      standards: selectedProgressionEntries,
    });
  }, [onChange, visibleGrades, selectedSubject, selectedDomain, selectedStrandFilter, selectedProgressionEntries]);

  const domainSections = useMemo(() => {
    const filteredRows = selectedStrandFilter
      ? searchableClusterRows.filter((row) => row.strandDescription === selectedStrandFilter)
      : searchableClusterRows;

    const sectionsMap = new Map();

    filteredRows.forEach((row) => {
      if (!sectionsMap.has(row.domain)) {
        sectionsMap.set(row.domain, {
          key: row.domain,
          name: row.domain,
          description: row.domainDescription,
          strands: new Map(),
        });
      }

      const section = sectionsMap.get(row.domain);
      const strandKey = row.strandKey || row.strand;
      if (!section.strands.has(strandKey)) {
        section.strands.set(strandKey, {
          key: strandKey,
          label: row.strandDescription || row.strand,  // Use description for orange header
          heading: '',  // Remove the heading row below
          clusters: new Map(),
        });
      }

      const strand = section.strands.get(strandKey);
      const clusterCode = String(row.cluster || '').trim();
      const clusterDescription = String(row.clusterDescription || '').trim();
      const normalizedClusterDescription = clusterDescription.toLowerCase().replace(/\s+/g, ' ');

      // Use stable cluster identity first so standards from the same cluster
      // stay grouped together even if description text varies slightly by row.
      const clusterKey = clusterCode || normalizedClusterDescription || 'uncategorized';

      if (!strand.clusters.has(clusterKey)) {
        strand.clusters.set(clusterKey, {
          key: clusterKey,
          label: clusterDescription || `Cluster ${clusterCode || 'uncategorized'}`,
          standards: [],
        });
      } else if (clusterDescription) {
        const existingCluster = strand.clusters.get(clusterKey);
        const existingLabel = String(existingCluster.label || '').trim();
        if (!existingLabel || existingLabel.toLowerCase().startsWith('cluster ')) {
          existingCluster.label = clusterDescription;
        }
      }

      strand.clusters.get(clusterKey).standards.push({
        key: row.id,
        grade: row.grade,
        applicableGrades: row.applicableGrades,
        code: row.code,
        description: row.description,
        // clusterExpectationDescription is what grade-entry receives; use the
        // resolved description so grade-entry always has descriptive text.
        clusterExpectationDescription: row.description || row.clusterDescription,
        cluster: row.cluster,
        clusterDescription: row.clusterDescription,
        subject: row.subject,
        domain: row.domain,
        strand: row.strand,
        strandDescription: row.strandDescription,
      });
    });

    return Array.from(sectionsMap.values()).map((section) => ({
      ...section,
      strands: Array.from(section.strands.values()).map((strand) => ({
        ...strand,
        clusters: Array.from(strand.clusters.values())
          .map((cluster) => {
          const columnsByGrade = new Map();
          cluster.standards.forEach((entry) => {
            const columnKey = getGradeColumnKey(entry.applicableGrades);
            if (!visibleColumnKeys.has(columnKey)) return;
            if (!columnsByGrade.has(columnKey)) columnsByGrade.set(columnKey, []);
            columnsByGrade.get(columnKey).push(entry);
          });

          const gradeColumns = Array.from(columnsByGrade.entries())
            .map(([grade, entries]) => ({
              grade,
              entries: entries
                .sort((left, right) => String(left.code).localeCompare(String(right.code), undefined, { numeric: true, sensitivity: 'base' })),
            }))
            .sort((left, right) => getGradeRank(left.grade) - getGradeRank(right.grade));

          return {
            ...cluster,
            gradeColumns,
          };
        })
          .sort((left, right) => {
            const leftGrades = left.standards.map((entry) => getGradeRank(entry.grade));
            const rightGrades = right.standards.map((entry) => getGradeRank(entry.grade));
            const leftMin = leftGrades.length ? Math.min(...leftGrades) : 999;
            const rightMin = rightGrades.length ? Math.min(...rightGrades) : 999;
            if (leftMin !== rightMin) return leftMin - rightMin;
            return String(left.label).localeCompare(String(right.label), undefined, { sensitivity: 'base' });
          }),
      })),
    }));
  }, [searchableClusterRows, selectedStrandFilter, visibleColumnKeys]);

  const selectedDomainHeading = useMemo(() => {
    if (!selectedDomain) {
      return 'All Domains';
    }
    return searchableClusterRows[0]?.domainDescription || selectedDomain;
  }, [searchableClusterRows, selectedDomain]);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const byStrand = new Map();
    searchableClusterRows.forEach((row) => {
      const strandKey = `${row.subject || ''}::${row.domain || ''}::${row.strand || ''}`;
      const clusterLabel = String(row.clusterDescription || row.cluster || '').trim();
      const normalizedLabel = normalizeClusterLabel(clusterLabel);
      if (!normalizedLabel) {
        return;
      }

      if (!byStrand.has(strandKey)) {
        byStrand.set(strandKey, new Map());
      }

      const labelVariants = byStrand.get(strandKey);
      if (!labelVariants.has(normalizedLabel)) {
        labelVariants.set(normalizedLabel, new Set());
      }
      labelVariants.get(normalizedLabel).add(clusterLabel);
    });

    const warnings = [];
    byStrand.forEach((normalizedMap, strandKey) => {
      normalizedMap.forEach((variants, normalizedLabel) => {
        if (variants.size > 1) {
          warnings.push({
            strandKey,
            normalizedLabel,
            variants: Array.from(variants.values()),
          });
        }
      });
    });

    if (warnings.length > 0) {
      console.warn('[Progression] Potential near-duplicate cluster labels detected.', warnings);
    }
  }, [searchableClusterRows]);

  useEffect(() => {
    if (!domainSections.length) {
      if (!data.length) return;
      setExpandedDomains({});
      setExpandedStrands({});
      return;
    }
    setExpandedDomains((previous) => {
      const nextDomainState = {};

      domainSections.forEach((section) => {
        const wasTracked = Object.prototype.hasOwnProperty.call(previous, section.key);
        const previousValue = wasTracked ? previous[section.key] : false;
        nextDomainState[section.key] = selectedDomain ? section.key === selectedDomain : previousValue;
      });

      return nextDomainState;
    });

    setExpandedStrands((previous) => {
      const nextStrandState = {};

      domainSections.forEach((section) => {
        section.strands.forEach((strand) => {
          const strandKey = `${section.key}::${strand.key}`;
          const wasTracked = Object.prototype.hasOwnProperty.call(previous, strandKey);
          const previousValue = wasTracked ? previous[strandKey] : false;
          const shouldOpenStrand = selectedStrandFilter
            ? (strand.label === selectedStrandFilter || strand.key === selectedStrandFilter)
            : previousValue;

          nextStrandState[strandKey] = shouldOpenStrand;
        });
      });

      return nextStrandState;
    });
  }, [data.length, domainSections, selectedDomain, selectedStrandFilter]);

  useEffect(() => {
    if (!focusedStandardCode || focusedStandardRef.current === focusedStandardCode) return;
    const frameId = window.requestAnimationFrame(() => {
      const matchingTiles = Array.from(document.querySelectorAll('[data-standard-code]')).filter(
        (element) => element.dataset.standardCode === focusedStandardCode
      );
      const target = matchingTiles.find(element => element.dataset.displayGrade === focusedColumnKey) || matchingTiles[0];
      if (!target) return;
      focusedStandardRef.current = focusedStandardCode;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [focusedStandardCode, focusedColumnKey, domainSections, expandedDomains, expandedStrands]);

  const toggleDomain = (domainKey) => {
    setExpandedDomains((previous) => ({
      ...previous,
      [domainKey]: !previous[domainKey],
    }));
  };

  const toggleStrand = (domainKey, strandKey) => {
    const compositeKey = `${domainKey}::${strandKey}`;
    setExpandedStrands((previous) => ({
      ...previous,
      [compositeKey]: !previous[compositeKey],
    }));
  };

  const handleTileClick = (entry) => {
    if (!entry || !allowCellInteraction) {
      return;
    }

    if (entry.subject === 'Attendance') {
      return;
    }

    const selection = createSelectionEntry(entry);

    if (enableContinueFlow) {
      setBulkSelectionColor('');
      setSelectedProgressionEntries((previous) => {
        const exists = previous.some((item) => item.id === selection.id);
        return exists ? previous.filter((item) => item.id !== selection.id) : [...previous, selection];
      });
      return;
    }

    const progressionState = {
      grade: selectedGrade,
      subject: selectedSubject,
      domain: selectedDomain,
      strand: selectedStrandFilter,
      expandedDomains,
      expandedStrands,
      focusedStandardCode: selection.code,
    };

    navigate('/admin/grade-entry', {
      state: {
        selection: {
          code: selection.code,
          subject: selection.subject,
          domain: selection.domain,
          cluster: selection.strand,
          standardDescription: selection.standardDescription,
          clusterExpectationDescription: selection.clusterExpectationDescription,
        },
        readOnly: true,
        progressionState,
        returnTo: {
          pathname: '/admin/standards-progression',
          state: { progressionState },
          label: '← Back to Standard Progressions',
        },
      },
    });
  };

  const toggleBulkSelection = (entries) => {
    const selections = entries
      .filter((entry) => entry && entry.subject !== 'Attendance')
      .map(createSelectionEntry);
    if (!selections.length) return;

    const selectedIds = new Set(selectedProgressionEntries.map((entry) => entry.id));
    const allSelected = selections.every((entry) => selectedIds.has(entry.id));
    if (allSelected) {
      const removeIds = new Set(selections.map((entry) => entry.id));
      setSelectedProgressionEntries((previous) => previous.filter((entry) => !removeIds.has(entry.id)));
      setBulkSelectionColor('');
      return;
    }

    setSelectedProgressionEntries((previous) => {
      const next = new Map(previous.map((entry) => [entry.id, entry]));
      selections.forEach((entry) => next.set(entry.id, entry));
      return Array.from(next.values());
    });
    setBulkSelectionColor('green');
  };

  const selectedPreviewCodes = selectedProgressionEntries
    .map((entry) => entry.code)
    .filter(Boolean)
    .slice(0, 3);
  const hiddenSelectedCount = Math.max(0, selectedProgressionEntries.length - selectedPreviewCodes.length);

  useEffect(() => {
    if (selectedProgressionEntries.length === 0) {
      setIsSelectionPanelCollapsed(false);
    }
  }, [selectedProgressionEntries]);

  const handleContinueToEntry = () => {
    if (!onContinue || selectedProgressionEntries.length === 0) return;
    onContinue(
      selectedProgressionEntries.map((entry) => ({ ...entry, grade: selectedGrade })),
      {
        initialScoreColor: bulkSelectionColor,
        progressionState: {
          grade: selectedGrade,
          subject: selectedSubject,
          domain: selectedDomain,
          strand: selectedStrandFilter,
          expandedDomains,
          expandedStrands,
          focusedStandardCode: selectedProgressionEntries[0]?.code || '',
        },
      },
    );
  };

  return (
    <div className="progression-explorer-shell">
      <h1 className="progression-title">{title}</h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 12 }}>
        <span style={{ fontWeight: 700, color: '#1e3a5f' }}>Standards View:</span>
        <button
          type="button"
          onClick={() => handleViewModeChange('all')}
          style={{
            padding: '4px 12px',
            borderRadius: 999,
            border: viewMode === 'all' ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
            background: viewMode === 'all' ? '#1d4ed8' : '#fff',
            color: viewMode === 'all' ? '#fff' : '#334155',
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Standard (All)
        </button>
        <button
          type="button"
          onClick={() => handleViewModeChange('preferred')}
          style={{
            padding: '4px 12px',
            borderRadius: 999,
            border: viewMode === 'preferred' ? '1px solid #1d4ed8' : '1px solid #cbd5e1',
            background: viewMode === 'preferred' ? '#1d4ed8' : '#fff',
            color: viewMode === 'preferred' ? '#fff' : '#334155',
            cursor: 'pointer',
            fontWeight: 700,
          }}
        >
          Standard Set
        </button>
        {viewMode === 'preferred' && (
          <>
            <select
              value={preferredTimeFrameType && preferredTimeFrameLabel ? `${preferredTimeFrameType}::${preferredTimeFrameLabel}` : ''}
              onChange={(event) => handleTimeFrameChange(event.target.value)}
              style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 12, background: '#ffffff' }}
            >
              <option value="">Select standard set</option>
              {availableTimeFrames.map((timeFrame) => (
                <option
                  key={`${timeFrame.time_frame_type}::${timeFrame.time_frame_label}`}
                  value={`${timeFrame.time_frame_type}::${timeFrame.time_frame_label}`}
                >
                  {timeFrame.time_frame_type}: {timeFrame.time_frame_label}{timeFrame.is_custom ? ' (Custom)' : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => {
                setEditingSet(null);
                setIsSetModalOpen(true);
              }}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid #1d4ed8',
                background: '#eff6ff',
                color: '#1d4ed8',
                cursor: 'pointer',
                fontWeight: 800,
                fontSize: 12,
              }}
            >
              + Create Standard Set
            </button>
            {activeCustomSet && (
              <button
                type="button"
                onClick={() => {
                  setEditingSet({
                    id: activeCustomSet.id,
                    subject: selectedSubject,
                    grade_band: selectedBandLabel,
                    time_frame_type: activeCustomSet.time_frame_type,
                    time_frame_label: activeCustomSet.time_frame_label,
                    mls_codes: activeCustomSet.mls_codes || [],
                  });
                  setIsSetModalOpen(true);
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 999,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#334155',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                ✏ Edit Set
              </button>
            )}
          </>
        )}
        {savingPreference && <span style={{ color: '#6b7280' }}>Saving…</span>}
        {viewMode === 'preferred' && !availableTimeFrames.length && timeFramesUnavailableReason === 'school_state_missing' && (
          <span style={{ color: '#9a3412' }}>
            Your school doesn't have a state assigned yet, so Standard Set view can't be configured. Ask your superadmin to set your school's state.
          </span>
        )}
        {viewMode === 'preferred' && !availableTimeFrames.length && timeFramesUnavailableReason !== 'school_state_missing' && (
          <span style={{ color: '#9a3412' }}>
            No standard sets created yet for {selectedSubject || 'this subject'} · {selectedBandLabel}. Click "+ Create Standard Set" to create one.
          </span>
        )}
        {preferenceError && <span style={{ color: '#b91c1c' }}>{preferenceError}</span>}
      </div>

      <StandardSetModal
        isOpen={isSetModalOpen}
        onClose={() => setIsSetModalOpen(false)}
        initialSubject={selectedSubject}
        initialGradeBand={selectedBandLabel}
        availableStandards={data}
        editingSet={editingSet}
        onSaved={handleSetModalSaved}
        onDeleted={handleSetModalDeleted}
      />

      <section className="progression-step-panel">
        <div className="progression-step-heading progression-step-heading-simple">
          <span className="progression-step-label">Refine the view</span>
        </div>
        <div className="progression-expanded-panel">
          <div className="progression-hierarchy-flow">
            <div className="progression-filter-stack progression-filter-stack-wide">
              <span className="progression-filter-stack-label">Grade Level</span>
              <div className="progression-pill-row" role="group" aria-label="Grade levels">
                {availableGrades.map((grade) => (
                  <button
                    key={grade}
                    type="button"
                    className={`progression-pill-button${selectedGrade === grade ? ' is-active' : ''}`}
                    aria-pressed={selectedGrade === grade}
                    onClick={() => setSelectedGrade(grade)}
                  >
                    {grade}
                  </button>
                ))}
              </div>
            </div>

            <div className="progression-filter-stack progression-filter-stack-wide">
              <span className="progression-filter-stack-label">Subject</span>
              <div className="progression-pill-row" role="group" aria-label="Subjects">
                {subjectCards.map((card) => {
                  const isActive = card.subject === selectedSubject;
                  return (
                    <button
                      key={card.subject}
                      type="button"
                      className={`progression-pill-button${isActive ? ' is-active' : ''}`}
                      onClick={() => {
                        setSelectedSubject(card.subject);
                        setSelectedDomain('');
                        setSelectedStrandFilter('');
                      }}
                    >
                      {card.shortLabel}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="progression-filter-stack progression-filter-stack-wide">
              <span className="progression-filter-stack-label">Domain</span>
              <div className="progression-pill-row" role="group" aria-label="Domains">
                <button
                  type="button"
                  className={`progression-pill-button${selectedDomain ? '' : ' is-active'}`}
                  onClick={() => {
                    setSelectedDomain('');
                    setSelectedStrandFilter('');
                  }}
                >
                  All Domains
                </button>
                {selectedSubjectWheel?.slices.map((slice) => (
                  <button
                    key={`${selectedSubjectWheel.subject}-${slice.domain}-flow`}
                    type="button"
                    className={`progression-pill-button${selectedDomain === slice.domain ? ' is-active' : ''}`}
                    onClick={() => {
                      setSelectedDomain(slice.domain);
                      setSelectedStrandFilter('');
                    }}
                  >
                    {slice.displayLabel}
                  </button>
                ))}
              </div>
            </div>

            <div className="progression-filter-stack progression-filter-stack-wide">
              <label className="progression-filter-stack-label" htmlFor="strand-filter-select">Strand</label>
              <select
                id="strand-filter-select"
                className="progression-inline-select"
                value={selectedStrandFilter}
                onChange={(event) => setSelectedStrandFilter(event.target.value)}
              >
                <option value="">All Strands</option>
                {strandOptions.map((strand) => (
                  <option key={strand} value={strand}>{strand}</option>
                ))}
              </select>
            </div>
          </div>
            <div className="progression-filter-controls">
              <div className="progression-filter-stack progression-filter-stack-search">
                <label className="progression-filter-stack-label" htmlFor="standard-search-input">Search Standards</label>
                <div className="progression-search-row">
                  <input
                    id="standard-search-input"
                    type="search"
                    className="progression-search-input"
                    placeholder="Search by code, description, domain, or strand"
                    value={standardSearchQuery}
                    onChange={(event) => setStandardSearchQuery(event.target.value)}
                  />
                  {standardSearchQuery && (
                    <button
                      type="button"
                      className="progression-search-clear"
                      onClick={() => setStandardSearchQuery('')}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="progression-filter-summary">
              Showing <strong>{selectedSubjectCard?.shortLabel || selectedSubject}</strong>
              {' · '}<strong>Grade {selectedGrade} (showing {visibleColumns.length ? visibleColumns.map((column) => column.key).join(', ') : selectedGrade})</strong>
              {' · '}<strong>{selectedDomainHeading}</strong>
              {selectedStrandFilter ? <><span>{' · '}</span><strong>{selectedStrandFilter}</strong></> : null}
              {standardSearchQuery.trim() ? <><span>{' · '}</span><strong>Search: {standardSearchQuery.trim()}</strong></> : null}
            </div>
          </div>
      </section>

      {domainSections.length === 0 ? (
        <div className="progression-placeholder">
          {selectedSubject
            ? standardSearchQuery.trim()
              ? `No standards match "${standardSearchQuery.trim()}" for the current filters.`
              : 'No progression rows are available for the current subject, grade band, and strand filters.'
            : 'Select a subject to view the progression board.'}
        </div>
      ) : (
        <div className="progression-domain-stack">
          {domainSections.map((section) => (
            <div key={section.key} className="progression-domain-section">
              <button
                type="button"
                className={`progression-domain-bar progression-domain-toggle${expandedDomains[section.key] ? ' is-open' : ''}`}
                onClick={() => toggleDomain(section.key)}
              >
                <div>
                  <div className="progression-domain-title">{section.name}</div>
                  {section.subtitle ? (
                    <div className="progression-domain-subtitle">{section.subtitle}</div>
                  ) : null}
                </div>
                <span className="progression-toggle-icon" aria-hidden="true">
                  {expandedDomains[section.key] ? '−' : '+'}
                </span>
              </button>

              {expandedDomains[section.key] && section.strands.map((strand) => {
                const strandCompositeKey = `${section.key}::${strand.key}`;
                const isStrandOpen = Boolean(expandedStrands[strandCompositeKey]);

                const strandEntries = Array.from(new Map(strand.clusters.flatMap((cluster) =>
                  cluster.gradeColumns.flatMap((col) => col.entries || [])
                ).map(entry => [entry.code, entry])).values());
                let strandGreen = 0; let strandLightGreen = 0; let strandYellow = 0; let strandRed = 0; let strandAssessed = 0;
                strandEntries.forEach((entry) => {
                  const pColor = progressByStandard[entry.code]?.color;
                  if (pColor === 'green') { strandGreen += 1; strandAssessed += 1; }
                  else if (pColor === 'lightgreen') { strandLightGreen += 1; strandAssessed += 1; }
                  else if (pColor === 'yellow') { strandYellow += 1; strandAssessed += 1; }
                  else if (pColor === 'red') { strandRed += 1; strandAssessed += 1; }
                });

                return (
                  <div key={`${section.key}-${strand.key}`} className="progression-strand-section">
                    <div style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
                      <button
                        type="button"
                        className={`progression-strand-bar progression-strand-toggle${isStrandOpen ? ' is-open' : ''}`}
                        onClick={() => toggleStrand(section.key, strand.key)}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span>{strand.label}</span>
                          {showProgress && strandAssessed > 0 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                              {strandGreen > 0 && <span style={{ background: '#22c55e', color: '#fff', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>🟢{strandGreen}</span>}
                              {strandLightGreen > 0 && <span title="On Track" style={{ background: 'var(--ae-status-on-track)', color: '#12203a', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>{strandLightGreen} On Track</span>}
                              {strandYellow > 0 && <span style={{ background: '#ffd84d', color: '#5b4a00', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>🟡{strandYellow}</span>}
                              {strandRed > 0 && <span style={{ background: '#ff7a79', color: '#fff', padding: '1px 6px', borderRadius: 999, fontSize: 10, fontWeight: 800 }}>🔴{strandRed}</span>}
                            </span>
                          )}
                        </span>
                        <span className="progression-strand-toggle-meta">
                          <span>Grade {selectedGrade}</span>
                          <span className="progression-toggle-icon" aria-hidden="true">{isStrandOpen ? '−' : '+'}</span>
                        </span>
                      </button>
                    </div>
                    {isStrandOpen && (() => {
                      const displayGrades = visibleColumns.length ? visibleColumns.map((column) => column.key) : [selectedGrade];
                      const gradeCount = Math.max(1, displayGrades.length);

                      return (
                        <React.Fragment>
                          <div className="progression-grade-carousel" role="group"
                            aria-label={`${section.name} ${strand.label} grade navigation`}
                            onKeyDown={event => {
                              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
                              event.preventDefault();
                              moveGrade(event.key === 'ArrowLeft' ? -1 : 1);
                            }}
                            onTouchStart={event => {
                              gradeSwipeStart.current = event.touches.length === 1
                                ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
                            }}
                            onTouchCancel={() => { gradeSwipeStart.current = null; }}
                            onTouchEnd={event => {
                              const start = gradeSwipeStart.current;
                              gradeSwipeStart.current = null;
                              if (!start || event.changedTouches.length !== 1) return;
                              const dx = event.changedTouches[0].clientX - start.x;
                              const dy = event.changedTouches[0].clientY - start.y;
                              if (Math.abs(dx) >= 60 && Math.abs(dx) > Math.abs(dy)) moveGrade(dx < 0 ? 1 : -1);
                            }}>
                            <button type="button" className="progression-grade-arrow"
                              aria-label="Previous grade" disabled={selectedColumnIndex === 0}
                              onClick={() => moveGrade(-1)}>‹</button>
                            <span className="progression-grade-carousel-caption" aria-live="polite">
                              <strong>Grade {selectedGrade}{focusedColumnKey !== selectedGrade ? ` · ${formatGradeColumnLabel(focusedColumnKey)}` : ''}</strong>
                              <span>Swipe here or use arrows to change grades</span>
                            </span>
                            <button type="button" className="progression-grade-arrow"
                              aria-label="Next grade" disabled={selectedColumnIndex >= gradeColumns.length - 1}
                              onClick={() => moveGrade(1)}>›</button>
                          </div>
                        <div className="progression-clusters-wrapper" style={{ marginTop: '12px' }}>
                          <div
                            className="progression-grade-board"
                            role="table"
                            aria-label={`${section.name} ${strand.label} progression`}
                            style={{
                              gridTemplateColumns: `repeat(${gradeCount}, minmax(160px, 1fr))`,
                              minWidth: `${gradeCount * 160}px`,
                            }}
                          >
                            {displayGrades.map((grade) => {
                              const clustersForGrade = strand.clusters
                                .map((cluster) => {
                                  const column = cluster.gradeColumns.find((item) => item.grade === grade);
                                  if (!column || !column.entries.length) {
                                    return null;
                                  }
                                  return {
                                    cluster,
                                    entries: column.entries,
                                  };
                                })
                                .filter(Boolean);

                              const gradeEntries = clustersForGrade.flatMap((item) => item.entries);
                              let gMastered = 0;
                              gradeEntries.forEach((entry) => {
                                const pColor = progressByStandard[entry.code]?.color;
                                if (pColor === 'green') {
                                  gMastered += 1;
                                }
                              });
                              const gTotal = gradeEntries.length;
                              const gMasteryPercent = gTotal > 0 ? Math.round((gMastered / gTotal) * 100) : 0;
                              const isFully = gTotal > 0 && gMastered === gTotal;
                              const isPartial = gMastered > 0 && !isFully;
                              const gradeSelectionEntries = gradeEntries.filter((entry) => entry.subject !== 'Attendance');
                              const selectedIds = new Set(selectedProgressionEntries.map((entry) => entry.id));
                              const allGradeStandardsSelected = gradeSelectionEntries.length > 0
                                && gradeSelectionEntries.every((entry) => selectedIds.has(entry.key));

                              return (
                                <div key={`${section.key}-${strand.key}-${grade}`} className="progression-grade-column" role="columnheader" aria-label={formatGradeColumnLabel(grade)}>
                                  <div className={`progression-th-grade${grade === focusedColumnKey ? ' progression-grade-focused' : ''}`} style={{ position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '6px 4px', background: showProgress && isFully ? '#22c55e' : showProgress && isPartial ? '#edf3ff' : undefined, color: showProgress && isFully ? '#fff' : '#1e3a8a' }}>
                                    {showProgress && isPartial && <div style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${gMasteryPercent}%`, background: 'linear-gradient(90deg, #bbf7d0 0%, #86efac 100%)', zIndex: 0 }} />}
                                    <button type="button" className="progression-grade-heading"
                                      aria-label={`Center on ${formatGradeColumnLabel(grade).toLowerCase()}`} aria-pressed={grade === focusedColumnKey}
                                      onClick={() => selectGradeColumn(gradeColumns.find((column) => column.key === grade))}>
                                      {formatGradeColumnLabel(grade)}{showProgress && isFully ? ' ✓' : ''}
                                    </button>
                                    {showProgress && <span style={{ position: 'relative', zIndex: 1, fontSize: 10, fontWeight: 800, opacity: isFully ? 0.95 : 1, color: isFully ? '#fff' : '#15803d' }}>
                                      {isFully ? `All Proficient (${gMastered}/${gTotal})` : `${gMastered}/${gTotal} Proficient (${gMasteryPercent}%)`}
                                    </span>}
                                    {enableContinueFlow && gradeSelectionEntries.length > 0 && (
                                      <button
                                        type="button"
                                        onClick={() => toggleBulkSelection(gradeSelectionEntries)}
                                        aria-pressed={allGradeStandardsSelected}
                                        disabled={!gradeSelectionEntries.length}
                                        title={`${allGradeStandardsSelected ? 'Deselect' : 'Select'} all ${gradeSelectionEntries.length} visible standards for ${formatGradeColumnLabel(grade)} in ${strand.label}${showScorePresetHint ? '; Green will be preselected in Data Entry' : ''}`}
                                        style={{ position: 'relative', zIndex: 1, marginTop: 3, border: '1px solid rgba(22, 101, 52, 0.35)', borderRadius: 6, padding: '3px 5px', background: allGradeStandardsSelected ? '#166534' : '#dcfce7', color: allGradeStandardsSelected ? '#fff' : '#166534', fontSize: 9, fontWeight: 800, cursor: 'pointer', lineHeight: 1.1 }}
                                      >
                                        {allGradeStandardsSelected ? 'Deselect all standards' : 'Select all standards'}
                                      </button>
                                    )}
                                  </div>
                                  <div className="progression-grade-column-body">
                                    {!clustersForGrade.length && <div className="progression-empty-grade">No standards for this grade in the current view.</div>}
                                    {clustersForGrade.map(({ cluster, entries }) => (
                                      <div key={`${section.key}-${strand.key}-${grade}-${cluster.key}`} className="progression-cluster-card">
                                        <div className="progression-cluster-grade-header" title={cluster.label}>
                                          {cluster.label}
                                        </div>
                                        <div className="progression-td-entry progression-td-stack">
                                          {entries.map((entry) => {
                                            const isSelected = selectedProgressionEntries.some((item) => item.id === entry.key);
                                            const progressLabel = progressByStandard[entry.code]?.label;
                                            const progressColor = progressByStandard[entry.code]?.color || 'neutral';
                                            const progressCount = Number(progressByStandard[entry.code]?.count || 0);
                                            const visibleDotCount = Math.min(progressCount, MAX_VISIBLE_DATA_POINT_DOTS);
                                            const remainingDotCount = Math.max(0, progressCount - visibleDotCount);
                                            const isAttendanceEntry = entry.subject === 'Attendance';
                                            const isEntryClickable = allowCellInteraction && !isAttendanceEntry;
                                            const isLongDescription = Boolean(entry.description && entry.description.length > 140);

                                            return (
                                              <div
                                                key={`${section.key}-${strand.key}-${cluster.key}-${grade}-${entry.key}`}
                                                data-standard-code={entry.code}
                                                data-display-grade={grade}
                                                className={`progression-standard-tile${isLongDescription ? ' is-long' : ''}${isSelected ? ' progression-standard-tile-selected' : ''}${entry.code === focusedStandardCode ? ' progression-standard-tile-published' : ''}${isEntryClickable ? ' progression-standard-tile-clickable' : ''}`}
                                                onClick={() => {
                                                  if (!isEntryClickable) return;
                                                  handleTileClick(entry);
                                                }}
                                              >
                                                <span className="progression-td-code">{entry.code}</span>
                                                {entry.description && <span className="progression-td-desc">{entry.description}</span>}
                                                {showProgress && <div className="progression-standard-dots" aria-label={`Data points for grade ${grade}`}>
                                                  <span className={`progression-data-dot progression-grade-dot is-${progressColor}`}>{grade}</span>
                                                  {progressCount > 0 ? (
                                                    <span
                                                      className="progression-data-dot-list"
                                                      aria-label={`${progressCount} data point${progressCount === 1 ? '' : 's'}`}
                                                    >
                                                      {Array.from({ length: visibleDotCount }).map((_, index) => (
                                                        <span
                                                          key={`${entry.key}-dot-${index}`}
                                                          className={`progression-data-dot progression-data-point-dot is-${progressColor}`}
                                                          aria-hidden="true"
                                                        />
                                                      ))}
                                                      {remainingDotCount > 0 ? (
                                                        <span
                                                          className="progression-data-dot-overflow"
                                                          aria-label={`${remainingDotCount} more data point${remainingDotCount === 1 ? '' : 's'}`}
                                                        >
                                                          +{remainingDotCount}
                                                        </span>
                                                      ) : null}
                                                    </span>
                                                  ) : null}
                                                </div>}
                                                {showProgress && progressLabel && (
                                                  <span className={`progression-progress-pill is-${String(progressLabel).toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                                                    {progressLabel}
                                                  </span>
                                                )}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        </React.Fragment>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {enableContinueFlow && selectedProgressionEntries.length > 0 && (
        <FloatingSelectionFooter>
        {!isSelectionPanelCollapsed ? (
        <div className="progression-selection-footer">
          <button
            type="button"
            className="progression-selection-close"
            aria-label="Collapse selected standards panel"
            onClick={() => setIsSelectionPanelCollapsed(true)}
          >
            ×
          </button>
          <div className="progression-selection-summary">
          <div className="progression-selection-title">Selected Standards</div>
          <div className="progression-selection-row">
            <span className="progression-selection-check">✓</span>
            <span className="progression-selection-text">
              <strong>{selectedProgressionEntries.length}</strong> standard{selectedProgressionEntries.length === 1 ? '' : 's'} selected
              <span className="progression-selection-codes" title={selectedProgressionEntries.map((entry) => entry.code || entry.id).join(', ')}>
              {selectedPreviewCodes.length > 0 ? `: ${selectedPreviewCodes.join(', ')}` : ''}
              {hiddenSelectedCount > 0 ? ` +${hiddenSelectedCount} more` : ''}
              </span>
            </span>
          </div>
          <div className="progression-selection-meta">
            Grade level: <strong>{selectedGrade}</strong>
          </div>
          </div>
          <button
            type="button"
            className="progression-continue-btn"
            onClick={handleContinueToEntry}
          >
            {continueLabel}
          </button>
        </div>
        ) : (
        <button
          type="button"
          className="progression-selection-reopen"
          onClick={() => setIsSelectionPanelCollapsed(false)}
        >
          Show selection ({selectedProgressionEntries.length})
        </button>
        )}
        </FloatingSelectionFooter>
      )}
      <BackToTop />
    </div>
  );
}